import { Link } from 'react-router-dom';
import { InfoPageLayout } from './InfoPageLayout';

export function VibeCodingTutorialPage() {
  return (
    <InfoPageLayout
      title="How to Vibe-Code an Application With Llambada"
      documentTitle="Vibe-Coding Tutorial"
    >
      <p>
        Vibe-coding means you describe what you want in plain language, test the
        result, then refine it in short feedback loops. This tutorial shows a
        simple process you can use every time.
      </p>

      <h2>Before You Start</h2>
      <ul>
        <li>Pick one clear problem to solve for one type of user.</li>
        <li>Define the input, output, and success condition in one sentence.</li>
        <li>Aim for a basic working version first, not a perfect first draft.</li>
      </ul>

      <h2>Step 1: Start With a Clear Goal</h2>
      <p>
        Open Llambada and create a new mini-app. Your first request should define
        what users provide and what they get back.
      </p>
      <p>
        Example: &quot;Build a tool where users paste a job description and get 5
        tailored resume bullet points for product manager roles.&quot;
      </p>

      <h2>Step 2: Write a Strong First Prompt</h2>
      <p>Use this structure to get better first-pass results:</p>
      <ol>
        <li>Purpose: what the app does</li>
        <li>Inputs: fields, file types, required options</li>
        <li>Output: exact format and tone</li>
        <li>UI flow: what appears first, next, and last</li>
        <li>Edge cases: empty input, errors, limits</li>
      </ol>
      <p>
        Helpful phrasing: &quot;Show validation errors inline, disable submit while
        processing, and show a copy button for results.&quot;
      </p>

      <h2>Step 3: Run and Review the First Version</h2>
      <p>
        Test the generated app immediately with realistic inputs. Focus on user
        behavior, not implementation details:
      </p>
      <ul>
        <li>Is the UI understandable in under 10 seconds?</li>
        <li>Does the output match the expected format?</li>
        <li>Are error states clear and actionable?</li>
      </ul>

      <h2>Step 4: Iterate With Small, Precise Changes</h2>
      <p>
        Avoid giant rewrite requests. Ask for one focused improvement per message.
      </p>
      <ul>
        <li>&quot;Add a character counter to the text area.&quot;</li>
        <li>&quot;Make result cards collapsible on mobile.&quot;</li>
        <li>&quot;Add CSV export for the generated list.&quot;</li>
      </ul>

      <h2>Step 5: Add Reliability and Guardrails</h2>
      <p>
        Make your app resilient before polishing visuals. Ask for explicit
        handling of failures and invalid input.
      </p>
      <ul>
        <li>Empty or malformed input messages</li>
        <li>Timeout and retry behavior</li>
        <li>Safe defaults when optional fields are missing</li>
      </ul>

      <h2>Step 6: Polish the Experience</h2>
      <p>
        Once behavior is solid, improve usability: labels, spacing, loading
        states, and helpful helper text.
      </p>
      <p>
        Example: &quot;Rewrite button labels to be action-based, improve spacing for
        mobile, and show a short success confirmation after copy/download.&quot;
      </p>

      <h2>Step 7: Test, Publish, and Improve</h2>
      <ol>
        <li>Test with 3-5 real-world inputs, including edge cases.</li>
        <li>Write a clear app title and description.</li>
        <li>Publish, collect feedback, and ship small improvements weekly.</li>
      </ol>

      <h2>Common Mistakes to Avoid</h2>
      <ul>
        <li>Trying to build every feature in one prompt</li>
        <li>Vague requests like &quot;make it better&quot; without specifics</li>
        <li>Skipping error-state and edge-case testing</li>
        <li>Polishing visuals before core functionality works</li>
      </ul>

      <h2>Next Steps</h2>
      <p>
        Continue with the <Link to="/guide">User Guide</Link> for deeper platform
        coverage, or return to the <Link to="/docs">Documentation index</Link> to
        choose another guide.
      </p>
    </InfoPageLayout>
  );
}
